import { useEffect, useState } from 'react';
import { subscriptionService, type SubscriptionData } from '../services/subscription.service';
import styles from './PaymentProcessing.module.css';

interface PaymentProcessingProps {
  getToken: () => Promise<string | undefined>;
  onComplete: (subscription: SubscriptionData) => void;
  onTimeout: () => void;
}

export function PaymentProcessing({ getToken, onComplete, onTimeout }: PaymentProcessingProps) {
  const [status, setStatus] = useState<'processing' | 'timeout'>('processing');
  const [dots, setDots] = useState('');

  useEffect(() => {
    // Animate dots
    const dotsInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => clearInterval(dotsInterval);
  }, []);

  useEffect(() => {
    const maxAttempts = 20; // 20 attempts over ~30 seconds
    let attempts = 0;
    const pollInterval = 1500; // 1.5 seconds
    let timeoutId: NodeJS.Timeout;

    const pollSubscription = async () => {
      try {
        const accessToken = await getToken();
        if (!accessToken) {
          setStatus('timeout');
          onTimeout();
          return;
        }

        const data = await subscriptionService.getSubscription(accessToken);

        // Check if subscription has been upgraded
        if (data.tier !== 'Free') {
          onComplete(data);
          return;
        }

        // Check if max attempts reached
        if (attempts >= maxAttempts) {
          setStatus('timeout');
          onTimeout();
          return;
        }

        // Continue polling
        attempts++;
        timeoutId = setTimeout(pollSubscription, pollInterval);
      } catch (err) {
        console.error('Error polling subscription:', err);

        if (attempts >= maxAttempts) {
          setStatus('timeout');
          onTimeout();
        } else {
          attempts++;
          timeoutId = setTimeout(pollSubscription, pollInterval);
        }
      }
    };

    pollSubscription();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [getToken, onComplete, onTimeout]);

  return (
    <div className={styles.backdrop}>
      <div className={styles.container}>
        {status === 'processing' ? (
          <>
            <div className={styles.spinner}>
              <div className={styles.spinnerRing}></div>
            </div>
            <h2 className={styles.title}>Processing Your Payment{dots}</h2>
            <p className={styles.message}>
              Your payment was successful! We're activating your subscription.
            </p>
            <p className={styles.submessage}>
              This usually takes just a few seconds.
            </p>
          </>
        ) : (
          <>
            <div className={styles.iconWarning}>⚠️</div>
            <h2 className={styles.title}>Taking Longer Than Expected</h2>
            <p className={styles.message}>
              Your payment was successful, but the subscription activation is taking longer than usual.
            </p>
            <p className={styles.submessage}>
              Please refresh the page in a moment to see your updated subscription.
            </p>
            <button
              onClick={() => window.location.reload()}
              className={styles.button}
            >
              Refresh Page
            </button>
          </>
        )}
      </div>
    </div>
  );
}